import { useCallback, useEffect, useRef, useState } from 'react';
import '../business-profile-setup.css';
import { businessProfileSetupGateway } from '../services/BusinessProfileSetupGateway';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { WIZARD_STEPS, type BusinessProfileResponse, type WizardStepKey } from '../types';
import { BasicInfoStep } from './steps/BasicInfoStep';
import { AddressStep } from './steps/AddressStep';
import { BankingStep } from './steps/BankingStep';
import { PayrollSetupStep } from './steps/PayrollSetupStep';
import { WorkLocationsStep } from './steps/WorkLocationsStep';
import { AdminInfoStep } from './steps/AdminInfoStep';

export interface StepProps {
  markStepComplete: (step: WizardStepKey) => void;
  profile: BusinessProfileResponse | null;
  refreshProfile: () => Promise<void>;
  /** True whenever this section's fields/actions must render as read-only information rather than
   * editable controls — the admin-edit default (View mode) before "Edit Company Details" is clicked, or
   * after "Cancel Changes"/a successful "Save Changes" returns to View mode. Always `false` in `setup`
   * mode, whose sections are editable immediately by design (there is no separate View mode concept for an
   * in-progress company that has nothing yet to view read-only). */
  readOnly: boolean;
  /** The page's own mode — most steps only need `readOnly`, but BankingStep also needs this directly: it
   * has its own secondary "is the edit form open" sub-state distinct from the page-level Edit mode, and
   * that sub-state's gating differs by mode (see BankingStep's own EPN-37 unified-edit-entry-point
   * correction notes). */
  mode: BusinessProfileSetupMode;
  /** Only used by WorkLocationsStep, whose Add/Edit Location sub-view has its own local Cancel/Save
   * footer (matching v9) and must hide the shared wizard footer underneath it while active. */
  setFooterHidden?: (hidden: boolean) => void;
  /** Only used by WorkLocationsStep. EPN-37 correction: the Main/Headquarters location is derived from the
   * Company's Business Address (Step 2) and was never independently editable here — clicking Edit on it now
   * jumps straight to Step 2 with Edit mode already active, instead of only showing an inline redirect
   * notice the admin then had to act on manually (navigate to the tab, then click Edit again). */
  onEditMainLocation?: () => void;
}

/** Every step exposes the same wizard-level actions to the shared footer, so the footer stays a single,
 * consistently-positioned control (matching v9's own single `.bp-footer`, outside every `.bp-step-section`)
 * instead of being duplicated — and styled slightly differently — inside each step. */
export interface StepHandle {
  /** Validates and saves this step through its own module-owned endpoint. On success, marks the step
   * complete and advances the wizard (or, on the last step, runs Final Submit). On failure, sets the
   * step's own inline error state and does not advance — the shell doesn't need to know which. Used by
   * `setup` mode only — see BusinessProfileSetupMode's own doc comment. */
  saveAndContinue: () => Promise<void>;
  /** Best-effort save without requiring full validation (mirrors v9's own Save and Exit, which never
   * blocks on validation) — never navigates. Every step now has its own module-owned draft representation
   * (Steps 1-2 in Identity.Api's CompanyProfileDraft, Step 3 in PaymentProcessing.Api's
   * CompanyFundingAccountDraft, Step 4 in PayrollConfig.Api's PayScheduleDraft, Step 5 in Identity.Api's
   * WorkLocationDraft). Save and Exit always persists only that unpromoted draft state — it never creates
   * or updates the canonical record, regardless of whether the current input happens to already be fully
   * valid. Only Save and Continue (saveAndContinue below) validates, writes the canonical record, clears
   * that step's draft, and advances. Step 3's draft deliberately never carries Routing/Account Number —
   * see CompanyFundingAccountDraft's own doc comment. Used by `setup` mode only.
   */
  saveAndExit: () => Promise<void>;
  /** admin-edit mode's "Save Changes" action, invoked only after the user confirms the page-level
   * confirmation modal. Validates and persists this step's edits through its own canonical endpoint,
   * exactly like saveAndContinue's own save call, but NEVER advances the wizard and NEVER runs Final
   * Submit (AdminInfoStep's commitEdits is a deliberate no-op — see its own comment — so reaching Admin
   * Information in admin-edit mode can never re-trigger company-completion submit() the way saveAndContinue
   * would). Work Locations and Admin Information have no deferred/batched field state to commit at all —
   * every one of their own mutations (Add/Archive/Reactivate Location, Send/Revoke Invitation, Owner
   * toggle) already saves immediately through its own dedicated confirmation, so their commitEdits is also
   * a no-op; "Save Changes" there simply closes edit mode. On failure, sets the step's own inline error
   * state (same mechanism saveAndContinue uses) and the page stays in edit mode. */
  commitEdits: () => Promise<void>;
  /** EPN-37 unsaved-changes correction: admin-edit only. Reports whether THIS section currently holds local
   * unsaved edits that switching to another section (via the stepper) would otherwise silently discard.
   * `goToStep` checks this only when `editingCompanyDetails` is already true (there is nothing to protect in
   * View mode) — optional and never called in `setup` mode, which has no separate Edit-mode concept to gate
   * on. Each step defines "dirty" for its own shape of local state (see each step's own implementation):
   * Basic Info/Address/Payroll Setup compare the current form to the baseline loaded on mount; Bank
   * Information (whose sensitive fields are always blank on entry — see BankingStep's own EPN-37
   * unified-edit-entry-point notes) is dirty if any of those blank fields now holds a value; Work Locations
   * is dirty only while its own Add/Edit Location sub-form is open AND differs from ITS OWN baseline (a
   * freshly opened, still-blank Add Location form is not yet "unsaved changes"); Admin Information is dirty
   * when an unsent Admin draft tab holds any meaningful (non-empty) field — its own existing discard-"×"
   * flow for that same draft is unaffected and still available directly on the tab. */
  isDirty?: () => boolean;
}

const STEP_COMPONENTS: Record<
  WizardStepKey,
  React.ForwardRefExoticComponent<StepProps & React.RefAttributes<StepHandle>>
> = {
  'basic-info': BasicInfoStep,
  address: AddressStep,
  banking: BankingStep,
  payroll: PayrollSetupStep,
  'work-locations': WorkLocationsStep,
  'admin-info': AdminInfoStep,
};

/**
 * `admin-edit` (the only mode wired to a route today, Settings > Company > Company Details): Company
 * Details and Business Profile Setup are the SAME underlying Business Profile data/components — there is
 * no separate Company Details data model (see this component's own EPN-37 admin-edit-mode correction
 * notes) — this mode is simply the contextual UX for viewing/editing an ALREADY-EXISTING company's data.
 * Every section already loads its own real canonical data independently (see each step's own mount
 * effect), so every one of the six sections is directly reachable immediately, with no Save and Continue
 * gate between them. The page defaults to a read-only View state; "Edit Company Details" (top-right of the
 * header) enters Edit mode for whichever section is open, replacing setup-oriented global actions (Clear
 * Draft, Save and Exit, Save and Continue/Finish Setup) with edit-oriented ones (Cancel Changes, Save
 * Changes) — see the render below and StepHandle.commitEdits's own doc comment.
 *
 * `setup` (not wired to any route yet — reserved for a future Super Admin "Add Company" flow, not built
 * in this ticket): the original v9 sequential stepper-locking behavior, preserved unchanged and
 * unconditionally available to whatever future caller passes this mode — a step is reachable only if
 * it's already been reached in this session, or the step immediately before it is complete, every section
 * is editable immediately (no View/Edit split — there is nothing yet to view read-only), and the footer
 * keeps its original Clear Draft/Go Back/Save and Exit/Save and Continue-Finish Setup actions. Final Submit
 * remains server-authoritative regardless of this frontend gating (see AdminInfoStep's saveAndContinue).
 */
export type BusinessProfileSetupMode = 'admin-edit' | 'setup';

/** Per-step copy for admin-edit's "Save Changes" confirmation — more specific wording for sections the
 * approved requirements call out as higher-consequence (bank information, legal identity/FEIN, and address
 * changes that can affect tax jurisdiction), generic copy otherwise. Work Locations and Admin Information
 * never actually commit anything through this modal (see StepHandle.commitEdits's own comment) but still
 * show the generic copy for a consistent Save Changes affordance. */
function saveChangesCopy(step: WizardStepKey): { title: string; body: string } {
  switch (step) {
    case 'basic-info':
      return {
        title: 'Confirm company information changes?',
        body: "This includes your company's legal name and FEIN, used for tax filings and legal identification. Review the updated information before confirming.",
      };
    case 'address':
      return {
        title: 'Confirm business address changes?',
        body: "This may affect payroll tax jurisdiction and filings tied to this company's address. Review the updated address before confirming.",
      };
    case 'banking':
      return {
        title: 'Confirm bank information changes?',
        body: "You're about to update the bank information used for payroll funding. Review the new information carefully before continuing.",
      };
    case 'payroll':
      return {
        title: 'Confirm pay schedule changes?',
        body: "This changes how and when this company's payroll is processed. Review the updated pay schedule before confirming.",
      };
    default:
      return { title: 'Confirm company changes?', body: "Review and confirm the updates to this company's information." };
  }
}

/** EPN-37 correction: "Edit Company Details" on every section sounded like it would edit the whole page at
 * once. The underlying edit mechanism is unchanged — still the single global admin-edit View/Edit toggle,
 * still one entry point — only the button's label now names the section it actually affects. Work Locations
 * and Admin Information use "Manage" rather than "Edit" since their own edit surface is really a set of
 * section-owned actions (Add/Archive/Reactivate Location, Send/Revoke Invitation, Owner toggle), not a
 * single field-level edit form. */
function editButtonLabel(step: WizardStepKey): string {
  switch (step) {
    case 'basic-info':
      return 'Edit Business Information';
    case 'address':
      return 'Edit Business Address';
    case 'banking':
      return 'Edit Bank Information';
    case 'payroll':
      return 'Edit Payroll Setup';
    case 'work-locations':
      return 'Manage Work Locations';
    case 'admin-info':
      return 'Manage Admins';
  }
}

/**
 * EPN-37 Business Profile Setup / Company Details — the approved 6-section Admin-facing screen. Renders
 * inside the app's own AppShell (unlike Admin Sign Up, which is a standalone pre-auth flow with its own
 * shell) — this feature is entirely on the normal authenticated session and permission model. See
 * BusinessProfileSetupMode's own doc comment for the two modes this component supports.
 */
export function BusinessProfileSetupPage({ mode = 'admin-edit' }: { mode?: BusinessProfileSetupMode }) {
  const [profile, setProfile] = useState<BusinessProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<WizardStepKey>('basic-info');
  const [completedSteps, setCompletedSteps] = useState<Set<WizardStepKey>>(new Set());
  const [resetKey, setResetKey] = useState(0);
  const [footerHidden, setFooterHidden] = useState(false);
  const [exitNotice, setExitNotice] = useState<string | null>(null);
  const [showClearDraftConfirm, setShowClearDraftConfirm] = useState(false);
  const [continuing, setContinuing] = useState(false);
  // admin-edit only — see BusinessProfileSetupMode's own doc comment. Always false in setup mode (every
  // section there is already editable with no separate View mode).
  const [editingCompanyDetails, setEditingCompanyDetails] = useState(false);
  const [showSaveChangesConfirm, setShowSaveChangesConfirm] = useState(false);
  // admin-edit only. Set only when goToStep intercepts a navigation away from a section with unsaved local
  // edits (see StepHandle.isDirty's own doc comment) — holds the target step index while the unsaved-
  // changes prompt (Save & Continue / Discard & Continue / Stay Here) below is open. Null the rest of the
  // time, including for every ordinary clean-section navigation, which still happens immediately exactly as
  // before.
  const [pendingNavigation, setPendingNavigation] = useState<{ index: number } | null>(null);
  const stepRef = useRef<StepHandle>(null);

  const refreshProfile = useCallback(async () => {
    const result = await businessProfileSetupGateway.getProfile();
    setProfile(result);
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      if (result.basicInfo.isComplete) next.add('basic-info');
      if (result.address.isComplete) next.add('address');
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await businessProfileSetupGateway.getProfile();
        if (cancelled) return;
        setProfile(result);
        const done = new Set<WizardStepKey>();
        if (result.basicInfo.isComplete) done.add('basic-info');
        if (result.address.isComplete) done.add('address');
        setCompletedSteps(done);
        if (mode === 'admin-edit') {
          // Every section is directly reachable immediately — see this component's own doc comment —
          // so there is no "first incomplete step" to land on; Basic Info is simply the first tab.
          setStep('basic-info');
        } else if (!result.basicInfo.isComplete) {
          setStep('basic-info');
        } else if (!result.address.isComplete) {
          setStep('address');
        } else {
          setStep('banking');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stepIndex = WIZARD_STEPS.findIndex((s) => s.key === step);
  const isLastStep = stepIndex === WIZARD_STEPS.length - 1;
  const markStepComplete = (s: WizardStepKey) => setCompletedSteps((prev) => new Set(prev).add(s));

  const canNavigateTo = (targetIndex: number): boolean => {
    // admin-edit: editing an already-existing company — every section already loads its own real
    // canonical data independently (see each step's own mount effect), so there is no "in-progress
    // setup" to gate sequentially. Every section is directly reachable immediately.
    if (mode === 'admin-edit') return true;
    if (targetIndex <= stepIndex) return true; // already reached
    const previousStep = WIZARD_STEPS[targetIndex - 1];
    return previousStep ? completedSteps.has(previousStep.key) : true;
  };

  /** Performs the actual navigation — the same unconditional behavior goToStep always had, now factored out
   * so both the clean-navigation path and Discard & Continue (below) share it. The step that's about to
   * unmount is the only one edit mode ever applied to (only one step is ever mounted at a time — see the
   * render below) — its own uncommitted edits are about to be discarded by that unmount regardless, so
   * leaving editingCompanyDetails on would just make the NEWLY mounted section incorrectly start in Edit
   * mode without the admin ever clicking its own Edit/Manage action for it. */
  const navigateToStep = (targetIndex: number) => {
    const target = WIZARD_STEPS[targetIndex];
    if (!target) return;
    setExitNotice(null);
    setEditingCompanyDetails(false);
    setStep(target.key);
  };

  /** EPN-37 unsaved-changes correction: admin-edit previously discarded whatever the open section's local
   * edits were the instant another stepper node was clicked — the remount `navigateToStep` triggers via
   * `key={step}-${resetKey}` already silently drops any local, not-yet-`commitEdits`-ed state. Now, only
   * when the CURRENT section is actually mid-edit and dirty (StepHandle.isDirty), that navigation is held
   * and the unsaved-changes prompt opens instead — clean navigation (the common case) is completely
   * unaffected, still immediate. Setup mode never reaches the dirty branch: it has no separate Edit mode
   * concept for `editingCompanyDetails` to gate on (always false there). */
  const goToStep = (targetIndex: number) => {
    const target = WIZARD_STEPS[targetIndex];
    if (!target || !canNavigateTo(targetIndex)) return;
    if (mode === 'admin-edit' && editingCompanyDetails && target.key !== step && stepRef.current?.isDirty?.()) {
      setPendingNavigation({ index: targetIndex });
      return;
    }
    navigateToStep(targetIndex);
  };

  const handleGoBack = () => goToStep(stepIndex - 1);

  /** EPN-37 Main Location correction: the Main/Headquarters work location is derived from the Company's
   * Business Address and was never independently editable in Work Locations — clicking Edit on it now jumps
   * straight to Step 2 with Edit mode already active in one action, rather than only showing an inline
   * redirect notice the admin then had to act on manually. Reachable only from Work Locations' own list view
   * (never its Add/Edit sub-form), which has no deferred local state of its own to protect — see
   * WorkLocationsStep's own isDirty, which is false whenever that list view, rather than the sub-form, is
   * showing — so this intentionally bypasses goToStep's own dirty-check/prompt rather than needing it. */
  const handleEditMainLocation = () => {
    const addressIndex = WIZARD_STEPS.findIndex((s) => s.key === 'address');
    if (addressIndex < 0) return;
    setExitNotice(null);
    setPendingNavigation(null);
    setStep('address');
    setEditingCompanyDetails(true);
  };

  /** Unsaved-changes prompt: Stay Here. Closes the prompt only — no state changes, current section stays in
   * Edit mode with every unsaved value exactly as the admin left it. */
  const handleStayHere = () => setPendingNavigation(null);

  /** Unsaved-changes prompt: Discard & Continue. Discards only the current section's local unsaved edits
   * (via the same remount-on-navigate mechanism every clean navigation already uses) and never calls a
   * canonical save endpoint. */
  const handleDiscardAndContinue = () => {
    if (!pendingNavigation) return;
    const { index } = pendingNavigation;
    setPendingNavigation(null);
    navigateToStep(index);
  };

  /** Unsaved-changes prompt: Save & Continue. Reuses the EXACT existing Save Changes pipeline (validate →
   * this section's own required confirmation, e.g. Bank Information's, if any → persist → close Edit mode)
   * so no section-specific confirmation is ever bypassed or duplicated — see handleConfirmSaveChanges below,
   * which navigates to `pendingNavigation`'s target only once that pipeline actually succeeds, and leaves
   * the admin on the current section with its inline validation errors, exactly as an ordinary Save Changes
   * failure already does, if it doesn't. */
  const handleSaveAndContinueFromPrompt = () => setShowSaveChangesConfirm(true);

  const handleSaveAndContinue = async () => {
    setContinuing(true);
    setExitNotice(null);
    try {
      await stepRef.current?.saveAndContinue();
      // The step resolved (no throw) — it saved successfully. Advance only if there's a next step; on
      // the last step "continue" IS Final Submit, handled entirely inside AdminInfoStep's own render.
      if (!isLastStep) {
        const next = WIZARD_STEPS[stepIndex + 1];
        if (next) setStep(next.key);
      }
    } catch {
      // The step already surfaced its own inline validation/API error — nothing further to do here.
    } finally {
      setContinuing(false);
    }
  };

  const handleSaveAndExit = async () => {
    setExitNotice(null);
    await stepRef.current?.saveAndExit();
    setExitNotice('Your progress has been saved. You can continue anytime.');
  };

  const handleClearDraft = async () => {
    setShowClearDraftConfirm(false);
    // Clears every step's own unpromoted draft state — Steps 1-2 (Identity.Api's CompanyProfileDraft),
    // Step 3 (PaymentProcessing.Api's CompanyFundingAccountDraft), Step 4 (PayrollConfig.Api's
    // PayScheduleDraft), and Step 5 (Identity.Api's WorkLocationDraft). None of these calls ever touch an
    // already-canonical Company/CompanyAddress/FundingAccount/PaySchedule/WorkLocation record.
    await Promise.all([
      businessProfileSetupGateway.clearDraft(),
      businessProfileSetupGateway.clearFundingAccountDraft(),
      businessProfileSetupGateway.clearPayScheduleDraft(),
      businessProfileSetupGateway.clearWorkLocationDraft(),
    ]);
    await refreshProfile();
    setResetKey((k) => k + 1); // remounts the active step so it re-reads canonical data fresh
    setExitNotice('Draft progress cleared. Already-saved profile data was not affected.');
  };

  /** admin-edit only. Discards whatever uncommitted local edits the currently-open section holds by
   * remounting it (the same proven mechanism handleClearDraft already uses) — since every step reloads its
   * own canonical data fresh on mount, this always "returns to View mode without modifying canonical
   * data," exactly as required, for every one of the six sections (including an in-progress, unsaved
   * Add/Edit Location sub-form or an unsent Admin draft tab, both purely local component state that a
   * remount naturally clears). Never touches canonical data itself — no gateway call here at all. */
  const handleCancelChanges = () => {
    setEditingCompanyDetails(false);
    setExitNotice(null);
    setPendingNavigation(null);
    setResetKey((k) => k + 1);
  };

  /** admin-edit only. Opens the Save Changes confirmation — the actual commit only happens after the user
   * confirms, in handleConfirmSaveChanges below. */
  const handleRequestSaveChanges = () => setShowSaveChangesConfirm(true);

  /** Shared by the ordinary footer Save Changes button AND the unsaved-changes prompt's own Save & Continue
   * (handleSaveAndContinueFromPrompt above) — both just open this same confirmation; this is where the
   * commit itself happens once it's confirmed. When reached via the unsaved-changes prompt, `pendingNavigation`
   * is still set: on success this also advances to that target section (already left in View mode, per
   * `commitEdits` clearing `editingCompanyDetails`) — "persist, exit Edit mode, navigate," exactly as
   * required. On failure it deliberately does NOT navigate or clear `pendingNavigation` — the admin stays on
   * the current section with its inline validation errors, and clicking the same stepper node again re-opens
   * this exact same flow. */
  const handleConfirmSaveChanges = async () => {
    setShowSaveChangesConfirm(false);
    setContinuing(true);
    try {
      await stepRef.current?.commitEdits();
      setEditingCompanyDetails(false);
      await refreshProfile();
      if (pendingNavigation) {
        const { index } = pendingNavigation;
        setPendingNavigation(null);
        navigateToStep(index);
      }
    } catch {
      // The step already surfaced its own inline validation/API error (same mechanism saveAndContinue
      // uses) — stay in Edit mode so the admin can correct the input and try again.
    } finally {
      setContinuing(false);
    }
  };

  const StepComponent = STEP_COMPONENTS[step];
  const isFullyCompleted = profile?.status === 'Completed';
  const readOnly = mode === 'admin-edit' ? !editingCompanyDetails : false;

  if (loading) {
    return (
      <div className="bps-shell" data-testid="business-profile-setup-page">
        <div className="bp-container">
          <p className="helper">Loading your business profile…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bps-shell" data-testid="business-profile-setup-page">
      <div className="bp-container">
        <div className="bp-header">
          <h2 className="title">{mode === 'admin-edit' ? 'Company Details' : 'Business Profile Setup'}</h2>
          {/* The "Completed" setup-status chip is meaningful only while a company is still going through
              setup — for an already-existing company's Company Details, there is nothing left to
              "complete," so it is never shown here. Setup mode keeps it unchanged. */}
          {mode !== 'admin-edit' && isFullyCompleted && <span className="status-chip">Completed</span>}
          {mode === 'admin-edit' && !editingCompanyDetails && (
            <button type="button" className="bp-btn bp-btn-secondary" onClick={() => setEditingCompanyDetails(true)}>
              <i className="fa fa-pencil bps-icon" aria-hidden="true" />
              {editButtonLabel(step)}
            </button>
          )}
        </div>

        <div className="bp-card">
          <nav className={`bp-stepper${mode === 'admin-edit' ? ' bp-stepper-all-complete' : ''}`} aria-label="Business Profile Setup steps">
            {WIZARD_STEPS.map((s, i) => {
              // admin-edit: every section represents an existing part of an already-configured company,
              // never onboarding progress not yet reached — so every node renders as done/green regardless
              // of whether it's been opened this session (see the `.bp-stepper-all-complete` CSS rules).
              // setup mode's true progress semantics (done/current/future) are completely unaffected.
              const done = mode === 'admin-edit' ? true : completedSteps.has(s.key);
              const active = s.key === step;
              const reachable = canNavigateTo(i);
              return (
                <button
                  key={s.key}
                  type="button"
                  className={`bp-step${done ? ' done' : ''}${active ? ' active' : ''}`}
                  aria-current={active ? 'step' : undefined}
                  aria-disabled={!reachable}
                  disabled={!reachable}
                  onClick={() => goToStep(i)}
                >
                  {/* Approved reference keeps the step number visible even once complete (only the fill
                      color changes) — never swaps it for a checkmark glyph. */}
                  <span className="bp-step-circle">{i + 1}</span>
                  <span className="bp-step-label">{s.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="bp-form-content">
            <div className="bp-step-section active">
              <StepComponent
                key={`${step}-${resetKey}`}
                ref={stepRef}
                markStepComplete={markStepComplete}
                profile={profile}
                refreshProfile={refreshProfile}
                readOnly={readOnly}
                mode={mode}
                setFooterHidden={setFooterHidden}
                onEditMainLocation={handleEditMainLocation}
              />
            </div>
          </div>

          {exitNotice && (
            <div className="notice" style={{ margin: '0 28px 12px' }}>
              {exitNotice}
            </div>
          )}

          {mode === 'setup' && !footerHidden && (
            <div className="bp-footer">
              <div className="footer-left">
                <button type="button" className="bp-btn bp-btn-clear" onClick={() => setShowClearDraftConfirm(true)}>
                  <i className="fa fa-trash-o bps-icon" aria-hidden="true" />
                  Clear Draft
                </button>
              </div>
              <div className="footer-right">
                <button type="button" className="bp-btn bp-btn-secondary" onClick={handleGoBack} style={{ visibility: stepIndex === 0 ? 'hidden' : 'visible' }}>
                  Go Back
                </button>
                <button type="button" className="bp-btn bp-btn-draft" onClick={() => void handleSaveAndExit()}>
                  Save and Exit
                </button>
                <button type="button" className="bp-btn bp-btn-primary" onClick={() => void handleSaveAndContinue()} disabled={continuing}>
                  {isLastStep ? 'Finish Setup' : 'Save and Continue'}
                </button>
              </div>
            </div>
          )}

          {mode === 'admin-edit' && editingCompanyDetails && !footerHidden && (
            <div className="bp-footer">
              <div className="footer-left">
                <button type="button" className="bp-btn bp-btn-secondary" onClick={handleCancelChanges}>
                  Cancel Changes
                </button>
              </div>
              <div className="footer-right">
                <button type="button" className="bp-btn bp-btn-primary" onClick={handleRequestSaveChanges} disabled={continuing}>
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={showClearDraftConfirm}
        title="Delete draft progress?"
        body="This clears only unsaved draft state across Steps 1-5. Already-saved profile data is not deleted."
        confirmLabel="Yes, Clear Draft"
        cancelLabel="Keep editing"
        onCancel={() => setShowClearDraftConfirm(false)}
        onConfirm={() => void handleClearDraft()}
      />

      <ConfirmDialog
        open={showSaveChangesConfirm}
        title={saveChangesCopy(step).title}
        body={saveChangesCopy(step).body}
        confirmLabel="Confirm Changes"
        onCancel={() => setShowSaveChangesConfirm(false)}
        onConfirm={() => void handleConfirmSaveChanges()}
      />

      {/* EPN-37 unsaved-changes correction: intercepts a stepper click away from a dirty section (see
          goToStep above) — Save & Continue re-opens the ordinary Save Changes confirmation above (any
          section-specific confirmation it requires still applies) and only navigates once that actually
          succeeds; Discard & Continue navigates immediately without touching canonical data; Stay Here just
          closes this prompt and leaves every unsaved value exactly as it was. */}
      <ConfirmDialog
        // `showSaveChangesConfirm` hides this one without clearing `pendingNavigation` itself — Save &
        // Continue (below) needs that target to still navigate to once the section-specific confirmation it
        // opens is actually confirmed (see handleConfirmSaveChanges). Without this, both dialogs would
        // render stacked at once the instant Save & Continue is clicked.
        open={pendingNavigation !== null && !showSaveChangesConfirm}
        title="You have unsaved changes"
        body={
          pendingNavigation
            ? `You have unsaved changes in ${WIZARD_STEPS[stepIndex]?.label ?? 'this section'}. Save them before moving to ${WIZARD_STEPS[pendingNavigation.index]?.label ?? 'the next section'}, discard them, or stay here to keep editing.`
            : ''
        }
        confirmLabel="Save & Continue"
        cancelLabel="Stay Here"
        tertiaryLabel="Discard & Continue"
        onTertiary={handleDiscardAndContinue}
        onCancel={handleStayHere}
        onConfirm={handleSaveAndContinueFromPrompt}
      />
    </div>
  );
}
